// AI assisted development
/**
 * Azure AD Authentication Service (environment-aware)
 */

import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  type Configuration,
  type AccountInfo,
  type SilentRequest,
} from '@azure/msal-browser';

import { AUTH_DISABLED } from '../config/auth';

// Environment variables (with safe defaults for dev-auth-disabled mode)
const clientId = import.meta.env.VITE_AZURE_AD_CLIENT_ID || 'development-client-id';
const tenantId = import.meta.env.VITE_AZURE_AD_TENANT_ID || 'common';
const apiScopeEnv = import.meta.env.VITE_API_SCOPE;  // <-- key line
const redirectUri = window.location.origin;

const isAzureAdConfigured =
  Boolean(import.meta.env.VITE_AZURE_AD_CLIENT_ID) &&
  Boolean(import.meta.env.VITE_AZURE_AD_TENANT_ID) &&
  import.meta.env.VITE_AZURE_AD_CLIENT_ID !== 'development-client-id';

const MSAL_INTERACTION_STATUS_KEY = 'msal.interaction.status';
const INTERACTION_IN_PROGRESS_ERROR = 'interaction_in_progress';
let redirectStarted = false;

// ⭐ NEW: API scope selection
// PROD: uses VITE_API_SCOPE injected by pipeline
// DEV: falls back to User.Read so nothing breaks
const apiScope = apiScopeEnv || "User.Read";

export const loginRequest = {
  scopes: ["openid", "profile", "email"], // no Graph scopes here
};

export const apiRequest = {
  scopes: [apiScope],
};

// MSAL configuration
const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri,
    postLogoutRedirectUri: redirectUri,
  },
  cache: {
    cacheLocation: "localStorage",
  },
  system: {
    loggerOptions: {
      loggerCallback: (_level: number, message: string, containsPii: boolean) => {
        if (!containsPii && import.meta.env.DEV) console.log(message);
      }
    }
  }
};

export const msalInstance = new PublicClientApplication(msalConfig);

export const initializeMsal = async () => {
  if (AUTH_DISABLED || !isAzureAdConfigured) return;
  await msalInstance.initialize();
  await msalInstance.handleRedirectPromise();
};

export const getAccount = (): AccountInfo | null => {
  const accounts = msalInstance.getAllAccounts();
  return accounts[0] || null;
};

const isInteractionInProgressError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const candidate = error as { errorCode?: string; message?: string };
  return (
    candidate.errorCode === INTERACTION_IN_PROGRESS_ERROR ||
    candidate.message?.includes(INTERACTION_IN_PROGRESS_ERROR) === true
  );
};

const clearStaleInteractionStatus = (): boolean => {
  if (typeof window === 'undefined') return false;

  let cleared = false;
  for (const storage of [window.localStorage, window.sessionStorage]) {
    const rawStatus = storage.getItem(MSAL_INTERACTION_STATUS_KEY);
    if (!rawStatus) continue;

    try {
      const status = JSON.parse(rawStatus) as { clientId?: string };
      if (status.clientId && status.clientId !== clientId) continue;
    } catch {
      // Older/corrupt MSAL state can block new redirects; clearing lets MSAL rebuild it.
    }

    storage.removeItem(MSAL_INTERACTION_STATUS_KEY);
    cleared = true;
  }

  return cleared;
};

export const signIn = async () => {
  if (AUTH_DISABLED || !isAzureAdConfigured) return null;
  const result = await msalInstance.loginPopup(loginRequest);
  return result.account;
};

export const signInRedirect = async () => {
  if (AUTH_DISABLED || !isAzureAdConfigured) return;
  if (redirectStarted) return;

  redirectStarted = true;
  try {
    await msalInstance.loginRedirect(loginRequest);
  } catch (error) {
    redirectStarted = false;
    if (isInteractionInProgressError(error) && clearStaleInteractionStatus()) {
      redirectStarted = true;
      await msalInstance.loginRedirect(loginRequest);
      return;
    }
    if (isInteractionInProgressError(error)) return;
    throw error;
  }
};

export const signOut = async () => {
  if (AUTH_DISABLED || !isAzureAdConfigured) return;
  const account = getAccount();
  if (account) await msalInstance.logoutPopup({ account });
};

export const getAccessToken = async (): Promise<string> => {
  // In dev mode with auth disabled, return empty token (API should accept unauthenticated requests)
  if (AUTH_DISABLED || !isAzureAdConfigured) return "";

  const account = getAccount();
  if (!account) throw new Error("No authenticated account");

  const request: SilentRequest = { ...apiRequest, account };

  try {
    return (await msalInstance.acquireTokenSilent(request)).accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      return (await msalInstance.acquireTokenPopup(request)).accessToken;
    }
    throw e;
  }
};

export const isAuthenticated = () => {
  // When auth is disabled, treat as authenticated so API calls proceed
  if (AUTH_DISABLED || !isAzureAdConfigured) return true;
  return getAccount() != null;
};

